import React, { useCallback, useEffect, useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BTN_PRIMARY, FailingAlert } from "@/components/postbacks/shared";
import { GuideLinks } from "@/components/postbacks/GuideLinks";
import { PostbackDialog } from "@/components/postbacks/PostbackDialog";
import { ConnectionDialog } from "@/components/postbacks/ConnectionDialog";
import { PostbackList } from "@/components/postbacks/PostbackList";
import { TrackingLinkHelp } from "@/components/postbacks/TrackingLinkHelp";

function Section({ title, hint, action, children, testId }) {
  return (
    <section className="brut-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="label-mono">{title}</div>
          <p className="text-sm text-zinc-700 mt-1 max-w-3xl">{hint}</p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function NewBtn({ show, onClick, testId, children }) {
  if (!show) return null;
  return (
    <button type="button" onClick={onClick} className={BTN_PRIMARY} data-testid={testId}>
      <Plus size={12} className="inline" /> {children}
    </button>
  );
}

export default function Postbacks() {
  const [meta, setMeta] = useState(null);
  const [editPb, setEditPb] = useState(undefined);
  const [editConn, setEditConn] = useState(undefined);
  const load = useCallback(async () => {
    const { data } = await api.get("/postbacks");
    setMeta(data);
  }, []);
  useEffect(() => {
    load().catch((err) => toast.error(err?.response?.data?.detail || "Couldn't load postbacks"));
  }, [load]);
  if (!meta) {
    return <div className="label-mono p-6" data-testid="postbacks-loading">Loading…</div>;
  }
  const saved = (msg) => () => {
    toast.success(msg);
    setEditPb(undefined);
    setEditConn(undefined);
    load();
  };
  const goLive = meta.needs_approval ? "sent for approval" : "is live";
  const failing = [...meta.postbacks, ...meta.connections].filter((r) => r.failing_since);
  return (
    <div className="space-y-6" data-testid="postbacks-page">
      <div>
        <div className="label-mono">Tracking</div>
        <h1 className="font-chivo font-black text-4xl sm:text-5xl tracking-tighter uppercase">Postbacks</h1>
        <p className="text-sm text-zinc-700 mt-2 max-w-3xl">
          Tell your tracker and ad accounts about clicks, leads and sales as they happen. Everything is sent from our
          server, so nothing slows down the pages your visitors see.
          {meta.needs_approval ? " New ones go live after an admin approves them." : " New ones go live as soon as you save them."}
        </p>
        <div className="mt-3"><GuideLinks /></div>
      </div>
      <FailingAlert rows={failing} testId="postbacks-failing-alert">
        <p className="text-xs text-red-800 mt-1">Check the details below, then press <strong>Send test</strong>.</p>
      </FailingAlert>
      <TrackingLinkHelp link={meta.link} ownerType={meta.owner_type} />
      <Section testId="postbacks-section" title="Step 2 · Postback links"
        hint="Our server calls your link the moment something happens. Nothing runs on the visitor's browser."
        action={<NewBtn show={meta.can_edit} onClick={() => setEditPb(null)} testId="new-postback-btn">New postback</NewBtn>}>
        <PostbackList rows={meta.postbacks} meta={meta} onEdit={setEditPb} onChanged={load} />
      </Section>
      <Section testId="connections-section" title="Optional · Ad platform connections"
        hint="Send the same moments straight to Meta, TikTok or Google Analytics from our server, instead of a browser pixel. Every new connection is checked by an admin first."
        action={<NewBtn show={meta.can_edit} onClick={() => setEditConn(null)} testId="new-connection-btn">New connection</NewBtn>}>
        <PostbackList rows={meta.connections} meta={meta} onEdit={setEditConn} onChanged={load} empty="No connections yet" />
      </Section>
      {!meta.can_edit && <div className="text-xs text-zinc-600" data-testid="postbacks-readonly">Only your company&apos;s admin can add or change these.</div>}
      {editPb !== undefined && (
        <PostbackDialog row={editPb} meta={meta} onClose={() => setEditPb(undefined)}
          onSaved={saved(editPb ? "Postback saved" : `Postback ${goLive}`)} />
      )}
      {editConn !== undefined && (
        <ConnectionDialog row={editConn} meta={meta} onClose={() => setEditConn(undefined)}
          onSaved={saved(editConn ? "Connection saved" : `Connection ${goLive}`)} />
      )}
    </div>
  );
}
